import { Button, Select, Spin, Table } from "antd";
import React, { useEffect, useState } from "react";
import { httpService } from "../../../services/httpService.service";
import previewIcon from "../../../Assets/svg/table-action-preview.svg";
import ExportButton from "../../../Assets/svg/exportButton.svg";
import FacilityTag from "../../components/FacilityTag/FacilityTag";
import { getSiteVisitStatus } from "../../../services/Enums/siteVisitStatusEnum";
import { formatDateAndTime } from "../../../services/helpFunctions/formatDateAndTime";
import { isAuthorized } from "../../../services/authService";
import { RolesEnum } from "../../../services/rolesEnum";
import ContractRangeSelectors from "../../components/ContractRamgeSelectors/ContractRangeSelectors";
import dayjs from "dayjs";

const { Option } = Select;

const buildQueryParams = (filters, pageSize, pageNumber) => {
  const params = new URLSearchParams();

  params.append("pageSize", pageSize);
  params.append("pageNumber", pageNumber);

  filters.projectIds.forEach((id) => {
    params.append("projectIds", id);
  });

  const { year, month, quarter } = filters.range;

  let startDate = null;
  let endDate = null;

  if (year && month) {
    startDate = dayjs(`${year}-${month}-01`).format("YYYY-MM-DD");
    endDate = dayjs(startDate).endOf("month").format("YYYY-MM-DD");
  } else if (year && quarter) {
    const qStart = (quarter - 1) * 3 + 1;
    startDate = dayjs(`${year}-${qStart}-01`).format("YYYY-MM-DD");
    endDate = dayjs(startDate)
      .add(2, "month")
      .endOf("month")
      .format("YYYY-MM-DD");
  } else if (year) {
    startDate = dayjs(`${year}-01-01`).format("YYYY-MM-DD");
    endDate = dayjs(`${year}-12-31`).format("YYYY-MM-DD");
  }

  if (startDate && endDate) {
    params.append("StartDate", startDate);
    params.append("EndDate", endDate);
  }

  return params.toString();
};

const getDateRangeFromFilters = (range) => {
  const { year, month, quarter } = range;

  if (!year) return { startDate: null, endDate: null };

  let startDate, endDate;

  if (month) {
    startDate = dayjs(`${year}-${month}-01`).format("YYYY-MM-DD");
    endDate = dayjs(startDate).endOf("month").format("YYYY-MM-DD");
  } else if (quarter) {
    startDate = dayjs(`${year}-${(quarter - 1) * 3 + 1}-01`).format(
      "YYYY-MM-DD"
    );
    endDate = dayjs(startDate)
      .add(2, "month")
      .endOf("month")
      .format("YYYY-MM-DD");
  } else {
    startDate = dayjs(`${year}-01-01`).format("YYYY-MM-DD");
    endDate = dayjs(`${year}-12-31`).format("YYYY-MM-DD");
  }

  return { startDate, endDate };
};

const generateTableColumns = (isLargeScreen, data, togglePreviewModal) => {
  return [
    {
      title: <span className="styledTitle">Site Visit Card Identifier</span>,
      dataIndex: "identifier",
      key: "identifier",
      ellipsis: true,
      width: 250,
      sorter: (a, b) => (a.identifier || "").localeCompare(b.identifier || ""),
      render: (record) => <span className="styledDataIndex3">{record}</span>,
    },
    {
      title: <span className="styledTitle">Site Name</span>,
      dataIndex: "name",
      key: "name",
      ellipsis: true,
      width: 250,
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
      render: (record) => <span className="styledDataIndex3">{record}</span>,
    },

    {
      title: <span className="styledTitle">Site Visit Status</span>,
      dataIndex: "tracking.siteVisitStatus",
      key: "siteVisitStatus",
      width: 250,
      sorter: (a, b) => {
        const statusA = a?.siteVisitStatus || "";
        const statusB = b?.siteVisitStatus || "";
        return String(statusA).localeCompare(String(statusB));
      },
      render: (text, record) => {
        const status = getSiteVisitStatus(record?.siteVisitStatus);
        return (
          <div className={status.className}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="8px"
              height="8px"
              viewBox="0 0 9 9"
            >
              <circle cx="4.5" cy="4.27344" r="4" fill={status.fill} />
            </svg>
            <span>{status.text}</span>
          </div>
        );
      },
    },
    {
      title: <span className="styledTitle">Date of Target Visit</span>,
      dataIndex: "visit.plannedVisit",
      key: "plannedVisit",
      sorter: (a, b) =>
        (a.visit?.plannedVisit || "").localeCompare(
          b.visit?.plannedVisit || ""
        ),
      render: (text, record) => (
        <span className="styledDataIndex3">
          {formatDateAndTime(record?.visit?.plannedVisit)}
        </span>
      ),
      ellipsis: true,
      width: 250,
    },
    {
      title: <span className="styledTitle">Facility</span>,
      dataIndex: "facility",
      key: "facility",
      ellipsis: true,
      width: 250,
      sorter: (a, b) =>
        (a.facility?.name || "").localeCompare(b.facility?.name || ""),
      render: (facility) => <FacilityTag facility={facility} />,
    },
    {
      title: <span className="styledTitle">Location</span>,
      sorter: (a, b) =>
        a.locationDetails?.country.localeCompare(b.locationDetails?.country),
      key: "location",
      dataIndex: "locationDetails.country",
      render: (text, record) => (
        <span className="styledDataIndex3">
          {record.locationDetails?.country}
        </span>
      ),
      ellipsis: true,
      width: 250,
    },
    {
      title: <span className="styledTitle">Governate/State</span>,
      dataIndex: "locationDetails.state",
      key: "state",
      ellipsis: true,
      width: 250,
      render: (text, record) => (
        <span className="styledDataIndex3">
          {record?.locationDetails?.state || "This country has no states."}
        </span>
      ),
    },
    {
      title: <span className="styledTitle">IP</span>,
      dataIndex: "ipPlan.implementingPartner.name",
      key: "implementingPartner",
      sorter: (a, b) =>
        (a.ipPlan?.implementingPartner?.name || "").localeCompare(
          b.ipPlan?.implementingPartner?.name || ""
        ),
      render: (text, record) => (
        <span className="styledDataIndex3">
          {record.ipPlan?.implementingPartner?.name}
        </span>
      ),
      ellipsis: true,
      width: 250,
    },

    {
      title: <span className="styledTitle">Cluster</span>,
      dataIndex: "cluster.name",
      key: "cluster",
      sorter: (a, b) =>
        (a.cluster?.name || "").localeCompare(b.cluster?.name || ""),
      render: (text, record) => (
        <span className="styledDataIndex3">{record.cluster?.name}</span>
      ),
      ellipsis: true,
      width: 250,
    },
    {
      key: "actions",
      fixed: isLargeScreen && data.length > 0 ? "right" : undefined,
      width: 120,
      render: (text, record) => (
        <div className="actionDiv">
          <img
            src={previewIcon}
            style={{ cursor: "pointer" }}
            alt="previewicon"
            onClick={() => togglePreviewModal(record.id)}
          />
        </div>
      ),
    },
  ];
};

const SiteVisitSummary = () => {
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth > 576);
  const [exportLoading, setExportLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projects, setProjects] = useState([]);
  const [siteVisits, setSiteVisits] = useState([]);
  const [filters, setFilters] = useState({
    projectIds: [],
    range: { year: null, month: null, quarter: null },
  });
  const [pagination, setPagination] = useState({
    pageSize: 10,
    current: 1,
    total: 0,
  });

  useEffect(() => {
    fetchProjectData();
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth > 576);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    fetchSiteVisits(pagination.pageSize, pagination.current);
  }, [filters, pagination.current, pagination.pageSize]);

  const handleRangeChange = (newValue) => {
    setFilters((prev) => ({
      ...prev,
      range: newValue,
    }));
  };

  const fetchProjectData = async () => {
    setLoadingProjects(true);
    const contractId = localStorage.getItem("currentContractId");
    httpService.get(
      `/api/Projects/getAll?contractId=${contractId}&pageSize=1000&pageNumber=1`,
      (response) => {
        setProjects(response?.data || []);
        setLoadingProjects(false);
      },
      (error) => {
        console.error("Error fetching project data:", error);
        setLoadingProjects(false);
      }
    );
  };

  const fetchSiteVisits = async (pageSize, pageNumber) => {
    setLoading(true);

    const queryString = buildQueryParams(filters, pageSize, pageNumber);
    const apiUrl = `/api/Site/getSiteVisitOfMultiplePorjects?${queryString}`;

    httpService.get(
      apiUrl,
      (response) => {
        const { data, metaData } = response || {};
        setSiteVisits(data || []);
        setPagination((prev) => ({
          ...prev,
          total: metaData?.totalItemCount || 0,
        }));
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching site visits:", error);
        setLoading(false);
      }
    );
  };

  const handlePageChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize,
    }));
  };

  const handleExport = () => {
    const { startDate, endDate } = getDateRangeFromFilters(filters.range);

    const params = {
      projectIds: filters.projectIds,
      startDate,
      endDate,
    };

    setExportLoading(true);

    httpService.post(
      "/api/Site/getSiteVisitOfMultiplePorjectsForExport",
      params,
      (response) => {
        const blob = new Blob([response], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Site_Visit_Summary.xlsx");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setExportLoading(false);
      },
      (error) => {
        console.error("Error exporting site visit summary:", error);
        setExportLoading(false);
      }
    );
  };

  return (
    <Spin spinning={loading} className="projectSiteVisitSummary">
      <div className="selectRounds">
        <div className="TPMSummaryTitle">
          Select project rounds to view their site visit summary
        </div>
        <Select
          mode="multiple"
          showSearch
          allowClear
          placeholder="Select rounds"
          loading={loadingProjects}
          value={filters.projectIds}
          onChange={(value) => {
            setFilters((prev) => ({
              ...prev,
              projectIds: value,
            }));
          }}
          style={{ width: "100%" }}
          optionFilterProp="children"
          filterOption={(input, option) =>
            option.children.toLowerCase().includes(input.toLowerCase())
          }
        >
          {projects.map((project) => (
            <Option key={project.id} value={project.id}>
              {project.projectName}
            </Option>
          ))}
        </Select>
      </div>
      {filters.projectIds.length <= 0 ? (
        <p className="my-4 w-full text-center">
          Please select at least one project round to view the site visit
          summary.
        </p>
      ) : (
        <>
          <div
            style={{
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
            }}
            className="p-3 m-5 my-4 border d-flex flex-column flex-lg-row align-items-center justify-content-between"
          >
            <div
              style={{ minWidth: "280px" }}
              className="d-flex flex-wrap flex-md-nowrap gap-5 align-items-center flex-1 justify-content-center justify-content-lg-start"
            >
              <ContractRangeSelectors
                enableQuarter={true}
                value={filters.range}
                onChange={handleRangeChange}
              />
            </div>
            {isAuthorized([
              RolesEnum.SystemAdmin,
              RolesEnum.ProjectManager,
              RolesEnum.FieldCoordinator,
              RolesEnum.FieldTeamLeader,
            ]) && (
              <Button
                style={{ alignSelf: "center" }}
                className="addButtonTPM d-flex align-items-center flex-nowrap gap-2 mt-3 mt-lg-0"
                onClick={handleExport}
                loading={exportLoading}
                disabled={exportLoading}
                icon={
                  !exportLoading && (
                    <img src={ExportButton} alt="ExportButton" />
                  )
                }
              >
                Export Site Visit Summary
              </Button>
            )}
          </div>
          <Table
            rowKey="id"
            columns={generateTableColumns(isLargeScreen, siteVisits, (id) => {
              console.log("Preview modal toggled for project ID:", id);
            })}
            dataSource={siteVisits}
            pagination={{
              ...pagination,
              onChange: handlePageChange,
            }}
            onChange={(pag) => setPagination(pag)}
            scroll={{ x: "max-content" }}
            className="mx-5 mb-5"
          />
        </>
      )}
    </Spin>
  );
};

export default SiteVisitSummary;
